import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { WagmiProvider, useAccount, useWalletClient, useChainId, useSwitchChain } from 'wagmi';
import { queryClient, wagmiAdapter, appKit, mendoza } from './lib/wallet';
import { createArkivWalletClient, saveCardToArkiv, getCardsByWallet, updateCardOnArkiv, deleteCardOnArkiv, getAllBusinessCards } from './lib/arkiv';
import {
  saveCardLocally,
  getLocalCardsByWallet,
  updateLocalCard,
  deleteLocalCard,
} from './lib/storage';
import type { BusinessCard } from './lib/types';
import { CardForm } from './components/CardForm';
import { CardList } from './components/CardList';
import { CardPreview } from './components/CardPreview';
import { CardGltfViewer } from './components/CardGltfViewer';
import toast, { Toaster } from 'react-hot-toast';

function App() {
  const [activeTab, setActiveTab] = useState<'create' | 'cards' | 'explore'>('create');
  const [cards, setCards] = useState<BusinessCard[]>([]);
  const [exploreCards, setExploreCards] = useState<BusinessCard[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExploreLoading, setIsExploreLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedCard, setSelectedCard] = useState<BusinessCard | null>(null);
  const [editingCard, setEditingCard] = useState<BusinessCard | null>(null);
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const [previewData, setPreviewData] = useState<Partial<BusinessCard> | null>(null);

  const { address, isConnected } = useAccount();
  const { data: walletClient, isLoading: isWalletLoading } = useWalletClient();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();

  // Handle PWA install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      console.log('Install prompt outcome:', outcome);
      setInstallPrompt(null);
    }
  };

  // Load cards when wallet connects
  useEffect(() => {
    if (isConnected && address) {
      loadCards();
    }
  }, [isConnected, address]);

  useEffect(() => {
    if (activeTab === 'explore' && exploreCards.length === 0) {
      loadExploreCards();
    }
  }, [activeTab]);

  useEffect(() => {
    if (!isConnected) {
      setActiveTab('explore');
      setEditingCard(null);
    }
  }, [isConnected]);

  const loadCards = async () => {
    if (!address) return;
    setIsLoading(true);
    try {
      const arkivCards = await getCardsByWallet(address);
      const localCards = await getLocalCardsByWallet(address);
      for (const localCard of localCards) {
        if (!localCard.arkivKey) {
          await deleteLocalCard(localCard.localId);
        }
      }
      for (const card of arkivCards) {
        await saveCardLocally(card);
      }
      setCards(arkivCards);
    } catch (error) {
      console.error('Error loading cards:', error);
      toast.error('Error loading cards. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadExploreCards = async () => {
    setIsExploreLoading(true);
    try {
      const all = await getAllBusinessCards(200);
      setExploreCards(all);
    } catch (error) {
      console.error('Error loading all cards:', error);
      toast.error('Could not load global cards.');
    } finally {
      setIsExploreLoading(false);
    }
  };

  const handleCreateCard = async (formData: Omit<BusinessCard, 'localId' | 'arkivKey' | 'walletAddress' | 'createdAt' | 'updatedAt'>, expiresAt?: Date) => {
    if (!address) {
      toast.error('Please connect your wallet first');
      return;
    }
    if (chainId !== mendoza.id) {
      try {
        await switchChain({ chainId: mendoza.id });
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        console.error('Failed to switch chain:', error);
        toast.error('Please switch to Arkiv Mendoza network in your wallet');
        return;
      }
    }
    if (isWalletLoading || !walletClient) {
      toast.error('Wallet is connecting, please try again in a moment');
      return;
    }

    setIsSaving(true);
    const optimisticCard: BusinessCard = {
      ...formData,
      localId: crypto.randomUUID(),
      walletAddress: address,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      expiresAt: expiresAt?.toISOString(),
      status: 'pending',
    };

    setCards(prevCards => [optimisticCard, ...prevCards]);
    setActiveTab('cards');

    try {
      const arkivClient = await createArkivWalletClient();
      const { entityKey, txHash } = await saveCardToArkiv(arkivClient, optimisticCard);
      const confirmedCard: BusinessCard = {
        ...optimisticCard,
        arkivKey: entityKey,
        status: 'confirmed',
      };
      await saveCardLocally(confirmedCard);
      setCards(prevCards =>
        prevCards.map(c => (c.localId === optimisticCard.localId ? confirmedCard : c))
      );
      toast.success('Business card saved successfully!');
      console.log('Card saved successfully:', { entityKey, txHash });
    } catch (error) {
      console.error('Error saving card:', error);
      toast.error('Failed to save card. Please try again.');
      setCards(prevCards => prevCards.filter(c => c.localId !== optimisticCard.localId));
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateCard = async (formData: Omit<BusinessCard, 'localId' | 'arkivKey' | 'walletAddress' | 'createdAt' | 'updatedAt'>, expiresAt?: Date) => {
    if (!editingCard || !editingCard.arkivKey) return;

    setIsSaving(true);
    const updatedCard: BusinessCard = {
      ...editingCard,
      ...formData,
      updatedAt: new Date().toISOString(),
      expiresAt: expiresAt?.toISOString(),
    };

    try {
      const arkivClient = await createArkivWalletClient();
      const newExpirationDate = expiresAt ?? (updatedCard.expiresAt ? new Date(updatedCard.expiresAt) : undefined);
      await updateCardOnArkiv(arkivClient, editingCard.arkivKey, updatedCard, newExpirationDate || undefined);
      await updateLocalCard(updatedCard);
      setCards(prevCards =>
        prevCards.map(c => (c.localId === updatedCard.localId ? updatedCard : c))
      );
      toast.success('Business card updated successfully!');
      setEditingCard(null);
    } catch (error) {
      console.error('Error updating card:', error);
      toast.error('Failed to update card. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCard = async () => {
    if (!editingCard || !editingCard.arkivKey) return;

    setIsSaving(true);
    try {
      const arkivClient = await createArkivWalletClient();
      await deleteCardOnArkiv(arkivClient, editingCard.arkivKey);
      await deleteLocalCard(editingCard.localId);
      setCards(prevCards => prevCards.filter(c => c.localId !== editingCard.localId));
      toast.success('Business card deleted successfully!');
      setEditingCard(null);
      setSelectedCard(null);
    } catch (error) {
      console.error('Error deleting card:', error);
      toast.error('Failed to delete card. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCardClick = (card: BusinessCard) => {
    setSelectedCard(card);
  };
  
  const openEditMode = (card: BusinessCard) => {
    setEditingCard(card);
    setSelectedCard(null);
    setActiveTab('create');
  }

  const canEditSelectedCard =
    !!selectedCard &&
    !!address &&
    selectedCard.walletAddress.toLowerCase() === address.toLowerCase() &&
    activeTab !== 'explore';

  const previewCard: BusinessCard = {
    localId: editingCard?.localId ?? 'preview-card',
    arkivKey: editingCard?.arkivKey,
    status: editingCard?.status ?? 'confirmed',
    name: previewData?.name ?? editingCard?.name ?? 'Your Name',
    title: previewData?.title ?? editingCard?.title ?? 'Role / Title',
    company: previewData?.company ?? editingCard?.company ?? 'Company or Collective',
    email: previewData?.email ?? editingCard?.email ?? 'you@company.xyz',
    phone: previewData?.phone ?? editingCard?.phone ?? '+00 000 0000',
    website: previewData?.website ?? editingCard?.website ?? '',
    bio: previewData?.bio ?? editingCard?.bio ?? '',
    social: previewData?.social ?? editingCard?.social ?? {
      linkedin: '',
      twitter: '',
      github: '',
    },
    location: previewData?.location ?? editingCard?.location ?? {
      address: 'Set your location to pin this card',
      coordinates: { lat: 0, lng: 0 },
    },
    walletAddress: address ?? editingCard?.walletAddress ?? '0xYourWallet',
    createdAt: editingCard?.createdAt ?? new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    expiresAt: previewData?.expiresAt ?? editingCard?.expiresAt,
  };

  return (
    <div className="relative min-h-screen bg-[#090f1e] px-4 py-10 text-slate-50">
      <div className="grid-overlay" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(14,165,233,0.16),transparent_35%),radial-gradient(circle_at_90%_0%,rgba(245,158,11,0.12),transparent_35%)] opacity-70" />
      <div className="relative mx-auto max-w-6xl">
        <div className="mb-8 flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-3">
            <div className="soft-badge">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              On-chain identity · Arkiv Mendoza
            </div>
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-white">Business Card Creator</h1>
              <p className="text-slate-300">Craft a card, pin it to the globe, and share it instantly.</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <appkit-button class="appkit-compact pill-wallet" />
          </div>
        </div>

        {!isConnected && (
          <div className="mb-8 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-sm text-slate-200">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-base font-semibold text-white">Browse global cards without connecting</p>
                <p className="text-slate-400">Explore the network freely. Connect your wallet when you want to create or manage your own cards.</p>
              </div>
              <appkit-button class="appkit-compact pill-wallet" />
            </div>
          </div>
        )}

        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="stat-card">
            <p className="text-xs uppercase tracking-wide text-slate-400">Total Cards</p>
            <p className="text-3xl font-semibold">{cards.length}</p>
            <p className="text-xs text-slate-400">Synced to your wallet</p>
          </div>
          <div className="stat-card">
            <p className="text-xs uppercase tracking-wide text-slate-400">Pending</p>
            <p className="text-3xl font-semibold">{cards.filter(c => c.status === 'pending').length}</p>
            <p className="text-xs text-slate-400">Awaiting confirmations</p>
          </div>
          <div className="stat-card">
            <p className="text-xs uppercase tracking-wide text-slate-400">Network</p>
            <p className="text-3xl font-semibold">Mendoza</p>
            <p className="text-xs text-slate-400">Live on Arkiv</p>
          </div>
        </div>

        <div className="mb-6 flex flex-wrap gap-2">
          <button
            onClick={() => { if (isConnected) { setActiveTab('create'); setEditingCard(null); } }}
            disabled={!isConnected}
            className={`tab ${activeTab === 'create' ? 'tab-active' : 'tab-inactive'} ${!isConnected ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {editingCard ? 'Edit Card' : 'Create Card'}
          </button>
          <button
            onClick={() => { if (isConnected) setActiveTab('cards'); }}
            disabled={!isConnected}
            className={`tab ${activeTab === 'cards' ? 'tab-active' : 'tab-inactive'} ${!isConnected ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            My Cards ({cards.length})
          </button>
          <button onClick={() => setActiveTab('explore')} className={`tab ${activeTab === 'explore' ? 'tab-active' : 'tab-inactive'}`}>
            Explore (global)
          </button>
        </div>

        {activeTab === 'create' ? (
          <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
            <div className="card p-6">
              <h2 className="mb-2 text-2xl font-semibold text-slate-900">{editingCard ? 'Edit Your Business Card' : 'Create Your Business Card'}</h2>
              <p className="mb-6 text-sm text-slate-600">Keep it concise and memorable—each field shows up in the live preview.</p>
              <CardForm
                mode={editingCard ? 'edit' : 'create'}
                initialData={editingCard || undefined}
                onSubmit={editingCard ? handleUpdateCard : handleCreateCard}
                onDelete={editingCard ? handleDeleteCard : undefined}
                onFormChange={setPreviewData}
                isLoading={isSaving}
              />
            </div>
            <div className="card relative overflow-hidden p-6">
              <div className="absolute inset-0 bg-gradient-to-br from-sky-500/10 via-emerald-400/5 to-amber-400/10" />
              <div className="relative">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">Live preview</p>
                    <h3 className="text-xl font-semibold text-slate-900">On-chain card</h3>
                  </div>
                  <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">Instant</span>
                </div>
                <CardPreview card={previewCard} className="shadow-2xl" />
                <p className="mt-4 text-sm text-slate-600">This is how your card will glow on the globe once saved.</p>
                <div className="mt-4">
                  <p className="mb-2 text-xs uppercase tracking-wide text-slate-500">3D glTF Render</p>
                  <CardGltfViewer card={previewCard} />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
          {activeTab === 'cards' ? (
            <div className="card p-6">
              <h2 className="mb-4 text-xl font-semibold">Your Business Cards</h2>
              <CardList cards={cards} onCardClick={handleCardClick} isLoading={isLoading} />
            </div>
          ) : (
            <div className="card p-6">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold">Explore Global Cards</h2>
                  <p className="text-sm text-slate-500">All public business cards on Arkiv</p>
                </div>
                <button className="btn btn-outline btn-sm" onClick={loadExploreCards} disabled={isExploreLoading}>
                  {isExploreLoading ? 'Refreshing…' : 'Refresh'}
                </button>
              </div>
              <div className="mb-6 rounded-xl border border-white/10 bg-slate-900/40 p-4">
                <p className="mb-2 text-xs uppercase tracking-wide text-slate-400">Spotlight</p>
                {selectedCard ? (
                  <CardGltfViewer card={selectedCard} />
                ) : exploreCards.length > 0 ? (
                  <CardGltfViewer card={exploreCards[0]} />
                ) : (
                  <div className="h-64 animate-pulse rounded-lg bg-white/5" />
                )}
              </div>
              <CardList cards={exploreCards} onCardClick={handleCardClick} isLoading={isExploreLoading} />
            </div>
          )}
        </>
      )}

        {selectedCard && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={() => setSelectedCard(null)}>
            <div className="card max-w-2xl animate-slide-up p-6" onClick={(e) => e.stopPropagation()}>
              <CardPreview card={selectedCard} className="mb-4" />
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setSelectedCard(null)}
                  className="btn btn-secondary btn-md px-5 border border-slate-200/80 bg-white/80 text-slate-900 hover:bg-white"
                >
                  Close
                </button>
                {canEditSelectedCard && (
                  <button
                    onClick={() => openEditMode(selectedCard)}
                    className="btn btn-primary btn-md px-5 shadow-lg"
                  >
                    Edit card
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function AppWrapper() {
  return (
    <WagmiProvider config={wagmiAdapter.wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <Toaster position="top-center" reverseOrder={false} />
        <App />
      </QueryClientProvider>
    </WagmiProvider>
  );
}

// Mount the app (prevent duplicate mounting)
const container = document.getElementById('root');
if (container && !container.hasChildNodes()) {
  const root = createRoot(container);
  root.render(<AppWrapper />);
}
